import type { UserSettings, QuestProgress } from "../data/types";
import type { MissionState } from "../meta/Missions";

export interface Inventory {
  boards: number;
  headstarts: number;
  boosters: number;
  shields: number;
  mysteryBoxes: number;
  superBoxes: number;
}

export interface PowerLevels {
  magnet: number;
  x2: number;
  sneakers: number;
  jetpack: number;
}

export interface WordHuntState {
  /** YYYY-MM-DD of the word being hunted */
  date: string;
  word: string;
  collected: number;
  streak: number;
  lastCompleted: string;
  completedTotal: number;
}

export interface SeasonHuntState {
  tokens: number;
  claimedTier: number;
}

export interface LifetimeStats {
  distance: number;
  coins: number;
  rides: number;
  escapes: number;
  boardsUsed: number;
  keys: number;
  missionsDone: number;
  jumps: number;
  dodges: number;
  runs: number;
  boxesOpened: number;
}

export interface SaveData {
  version: number;
  playerName: string;
  highScore: number;
  bestDistance: number;
  totalCoins: number;
  lifetimeDistance: number;
  runs: number;
  xp: number;
  level: number;
  selectedCarId: string;
  unlockedCars: string[];
  quests: QuestProgress[];
  settings: UserSettings;
  /** keys continue a run; boards save you from one crash */
  keys: number;
  boards: number;
  tutorialSeen: boolean;
  missions: MissionState;
  /** the permanent multiplier from finished mission sets (x1 … x30) */
  multiplier: number;
  inventory: Inventory;
  powerLevels: PowerLevels;
  selectedBoardId: string;
  unlockedBoards: string[];
  wordHunt: WordHuntState;
  seasonHunt: SeasonHuntState;
  achievements: Record<string, number>;
  stats: LifetimeStats;
  /** ms since 1970 of the last write on this device (cloud sync: which wallet is newer) */
  savedAt: number;
  /** shop activity since the last run: counted into the "spend SOL" / "buy boxes" missions when the next run ends */
  pendingMeta: { spentCoins: number; boughtBoxes: number };
}

const defaultSettings: UserSettings = {
  music: 0.7,                          // 0.4 sat ~8 dB under the sound effects (Karolis 2026-10-17: "the music was really quiet")
  sfx: 0.75,
  reducedMotion: false,
  leftHanded: false,
  tapControls: false,
  screenShake: true,
  highContrast: false,
  sensitivity: 1,
  timeOfDay: "day"
};

const defaults: SaveData = {
  version: 2,
  playerName: "",
  highScore: 0,
  bestDistance: 0,
  totalCoins: 0,
  lifetimeDistance: 0,
  runs: 0,
  xp: 0,
  level: 1,
  selectedCarId: "pearl",
  unlockedCars: ["pearl"],
  quests: [],
  settings: defaultSettings,
  keys: 3,
  boards: 2,
  tutorialSeen: false,
  missions: { setIndex: 0, progress: [0, 0, 0], done: [false, false, false] },
  multiplier: 1,
  inventory: { boards: 2, headstarts: 1, boosters: 1, shields: 0, mysteryBoxes: 0, superBoxes: 0 },
  powerLevels: { magnet: 0, x2: 0, sneakers: 0, jetpack: 0 },
  selectedBoardId: "default",
  unlockedBoards: ["default"],
  wordHunt: { date: "", word: "", collected: 0, streak: 0, lastCompleted: "", completedTotal: 0 },
  seasonHunt: { tokens: 0, claimedTier: 0 },
  achievements: {},
  stats: { distance: 0, coins: 0, rides: 0, escapes: 0, boardsUsed: 0, keys: 0, missionsDone: 0, jumps: 0, dodges: 0, runs: 0, boxesOpened: 0 },
  savedAt: 0,
  pendingMeta: { spentCoins: 0, boughtBoxes: 0 }
};

/** A fresh save (cloud sync uses it to tell "nothing played here yet"). */
export const defaultSave = (): SaveData => structuredClone(defaults);

export class SaveManager {
  private readonly key = "solrun-save-v1";
  data: SaveData;
  /** called after every write (it fed the cloud save, archived with Sign in with X at the tag solrun-x-signin) */
  onSaved?: () => void;

  constructor() {
    this.data = this.load();
  }

  private load(): SaveData {
    try {
      const raw = localStorage.getItem(this.key);
      if (!raw) return structuredClone(defaults);
      return SaveManager.migrate(JSON.parse(raw) as Partial<SaveData>);
    } catch {
      return structuredClone(defaults);
    }
  }

  private static migrate(parsed: Partial<SaveData>): SaveData {
    const data: SaveData = { ...structuredClone(defaults), ...parsed, settings: { ...defaultSettings, ...parsed.settings } };
    if (data.settings.music === 0.4) data.settings.music = 0.7;      // the old default (too quiet) → the new one; a value someone moved stays
    // v1 → v2: nested records that did not exist get their defaults; the old loose board count moves into the inventory
    for (const key of ["missions", "inventory", "powerLevels", "wordHunt", "seasonHunt", "stats", "pendingMeta"] as const) {
      (data as unknown as Record<string, unknown>)[key] = { ...(defaults[key] as object), ...((parsed[key] as object | undefined) ?? {}) };
    }
    if ((parsed.version ?? 1) < 2 && typeof parsed.boards === "number") data.inventory.boards = parsed.boards;
    // the garage became roadster paint jobs (2026-10-16): old car ids fall back to Pearl
    const paints = ["pearl", "midnight", "red", "blue", "solana", "excel", "carbon", "flames", "holo", "gold"];
    if (!paints.includes(data.selectedCarId)) data.selectedCarId = "pearl";
    data.unlockedCars = data.unlockedCars.filter((id) => paints.includes(id));
    if (!data.unlockedCars.includes("pearl")) data.unlockedCars.push("pearl");
    data.version = 2;
    if (typeof data.savedAt !== "number") data.savedAt = 0;
    // time of day (2026-10-17): older saves have none (→ day, the look they know); anything unknown is day too
    if (!["day", "night", "auto"].includes(data.settings.timeOfDay)) data.settings.timeOfDay = "day";
    return data;
  }

  save(): void {
    this.data.savedAt = Date.now();
    try { localStorage.setItem(this.key, JSON.stringify(this.data)); } catch { /* private mode: keep going */ }
    this.onSaved?.();
  }

  /** Replaces the whole save (a merged cloud save) and writes it, without re-triggering a push. */
  replace(data: SaveData): void {
    this.data = data;
    try { localStorage.setItem(this.key, JSON.stringify(this.data)); } catch { /* private mode */ }
  }

  /** The stored JSON parsed with today's defaults and migrations (the cloud copy goes through the same door). */
  static normalize(parsed: Partial<SaveData>): SaveData {
    return SaveManager.migrate(parsed);
  }

  /** Records a finished run; returns true when it is a new personal best. */
  commitRun(score: number, coins: number, distance: number, questXp: number, keys = 0): boolean {
    this.data.keys += keys;
    this.data.stats.runs += 1;
    this.data.stats.distance += distance;
    this.data.stats.coins += coins;
    this.data.stats.keys += keys;
    const isHigh = score > this.data.highScore;
    this.data.highScore = Math.max(this.data.highScore, score);
    this.data.bestDistance = Math.max(this.data.bestDistance, distance);
    this.data.totalCoins += coins;
    this.data.lifetimeDistance += distance;
    this.data.runs += 1;
    this.data.xp += questXp + Math.floor(distance / 50);
    while (this.data.xp >= this.data.level * 250) {
      this.data.xp -= this.data.level * 250;
      this.data.level += 1;
    }
    this.save();
    return isHigh;
  }

  /** Undoes a commitRun when the run continues after a revive; the run is committed again when it really ends. */
  uncommitRun(coins: number, distance: number, questXp: number): void {
    this.data.totalCoins = Math.max(0, this.data.totalCoins - coins);
    this.data.lifetimeDistance = Math.max(0, this.data.lifetimeDistance - distance);
    this.data.runs = Math.max(0, this.data.runs - 1);
    this.data.xp = Math.max(0, this.data.xp - questXp - Math.floor(distance / 50));
    this.save();
  }

  reset(): void {
    this.data = structuredClone(defaults);
    this.save();
  }
}
